"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useMedia } from "../context/MediaContext"
import { useSocketInterview } from "../hooks/useSocketInterview"
import { useSelector } from "react-redux"
import { selectUserName } from "../slice/aiInterviewSlice"
import { InterviewHeader } from "./InterviewHeader"
import { LocalPreview } from "./LocalPreview"
import { CallControls } from "./CallControls"
import { TranscriptPanel } from "./TranscriptPanel"
import { MicWarningModal } from "./MicWarningModal"
import { EndInterviewModal } from "./EndInterviewModal"
import { useRecording } from "../hooks/useRecording"
import { useInterviewMedia } from "../hooks/useInterviewMedia"
import { useInterviewCleanup } from "../hooks/useInterviewCleanup"
import { useSpeechRecognition } from "../hooks/useSpeechRecognition"

const InterviewCallV2: React.FC = () => {
    const navigate = useNavigate()
    const { stream, setStream, isCameraOn, setIsCameraOn, isMicOn, setIsMicOn, videoRef } = useMedia()
    const [viewLiveTranscript, setViewLiveTranscript] = useState<boolean>(true)
    const [showMicWarningModal, setShowMicWarningModal] = useState(false)
    const [isVisibleEndInterviewOpen, setIsVisibleEndInterviewOpen] = useState(false)
    const jobTitle = useSelector((state: any) => state.aiInterview.jobTitle) || "AI Interview"
    const userName = useSelector(selectUserName)

    const [elapsedTime, setElapsedTime] = useState(0)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const isInterviewEndedRef = useRef(false) // Track if interview has ended

    // Check if invitation ID exists on mount
    useEffect(() => {
        const invitationId = localStorage.getItem('invitationId')
        if (!invitationId) {
            navigate('/invitationNotValid')
        }
    }, [navigate])

    const {
        connected,
        messages,
        isAiTyping,
        isMyraSpeaking,
        error: socketError,
        aiEndCall,
        sendMessage: originalSendMessage,
        sendUserEndCall,
        disconnect,
        stopTTS,
    } = useSocketInterview()

    // Recording hook
    const {
        isRecording,
        displayStreamRef,
        micStreamRef,
        audioContextRef,
        destinationRef,
        micSourceNodeRef,
        micGainNodeRef,
        startSimpleRecording,
        stopRecording,
        waitForRecordingFinalize,
        getFinalRecordingUrl,
        muteMicInRecording,
        unmuteMicInRecording,
    } = useRecording()

    const lastAiMessageRef = useRef<string>("")
    const lastAiMessageTimeRef = useRef<number>(0)
    const lastUserMessageTimeRef = useRef<number>(0)
    const isProcessingRef = useRef(false)

    const sendMessage = useCallback(
        (content: string) => {
            if (isMyraSpeaking) {
                return
            }

            const timeSinceLastAi = Date.now() - lastAiMessageTimeRef.current
            if (timeSinceLastAi < 5000 && lastAiMessageRef.current) {
                const similarity = calculateSimilarity(content.toLowerCase(), lastAiMessageRef.current.toLowerCase())
                if (similarity > 0.6) {
                    return
                }
            }

            isProcessingRef.current = true
            lastUserMessageTimeRef.current = Date.now()
            originalSendMessage(content)
        },
        [originalSendMessage, isMyraSpeaking],
    )

    const calculateSimilarity = (str1: string, str2: string): number => {
        const words1 = str1.split(/\s+/)
        const words2 = str2.split(/\s+/)
        const commonWords = words1.filter((word) => words2.includes(word) && word.length > 3)
        return commonWords.length / Math.max(words1.length, words2.length)
    }

    // Speech recognition hook
    const recognitionRef = useRef<any>(null)
    const audioTrackRef = useRef<MediaStreamTrack | null>(null)

    const { isListening, stopRecognition, isBlockedFromSpeakingRef } = useSpeechRecognition({
        isMicOn,
        connected,
        isMyraSpeaking,
        stream,
        audioTrackRef,
        sendMessage,
        muteMicInRecording,
        unmuteMicInRecording,
    })

    // Interview media hook
    const { isCameraStarting, isMicStarting, audioTrackRef: mediaAudioTrackRef, toggleCamera, toggleMicrophone } = useInterviewMedia({
        stream,
        setStream,
        videoRef,
        isCameraOn,
        setIsCameraOn,
        isMicOn,
        setIsMicOn,
        connected,
        isMyraSpeaking,
        isBlockedFromSpeakingRef,
        recognitionRef,
        setIsListening: () => { },
        micStreamRef,
        audioContextRef,
        destinationRef,
        micSourceNodeRef,
        micGainNodeRef,
    })

    // Sync audioTrackRef between hooks
    useEffect(() => {
        audioTrackRef.current = mediaAudioTrackRef.current
    }, [mediaAudioTrackRef])

    // Interview cleanup hook
    const { performEndInterview, wasTerminatedByScreenShareRef } = useInterviewCleanup({
        stream,
        setStream,
        setIsCameraOn,
        setIsMicOn,
        setIsRecording: () => { },
        videoRef,
        stopTTS,
        disconnect,
        stopRecording,
        waitForRecordingFinalize,
        getFinalRecordingUrl,
        stopRecognition,
        displayStreamRef,
        micStreamRef,
        audioTrackRef,
        audioContextRef,
        destinationRef,
        micSourceNodeRef,
        micGainNodeRef,
        timerRef,
    })

    // Update last AI message
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1]
            if (lastMessage.from === "ai") {
                lastAiMessageRef.current = lastMessage.content
                lastAiMessageTimeRef.current = Date.now()
                isProcessingRef.current = false
            }
        }
    }, [messages])

    // Timer effect
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setElapsedTime((prev) => prev + 1)
        }, 1000)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [])

    // Start recording when connected
    useEffect(() => {
        if (connected && !isRecording) {
            startSimpleRecording()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected])

    // Before unload warning
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            const shouldWarn = connected || isRecording || isMicOn || isCameraOn
            if (!shouldWarn) return

            e.preventDefault()
            e.returnValue = ''
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => window.removeEventListener("beforeunload", handleBeforeUnload)
    }, [connected, isRecording, isMicOn, isCameraOn])

    // Handle AI ending the call
    useEffect(() => {
        if (aiEndCall && !isInterviewEndedRef.current) {
            isInterviewEndedRef.current = true // Mark interview as ended
            sendUserEndCall()
            performEndInterview(elapsedTime, messages)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiEndCall])

    // Monitor screen share stop
    useEffect(() => {
        if (!displayStreamRef.current) return

        const videoTracks = displayStreamRef.current.getVideoTracks()
        if (videoTracks.length === 0) return

        const videoTrack = videoTracks[0]

        const handleScreenShareStop = async () => {
            if (isInterviewEndedRef.current) return // Already ended, don't trigger again

            isInterviewEndedRef.current = true // Mark interview as ended
            wasTerminatedByScreenShareRef.current = true

            if (isMyraSpeaking) {
                stopTTS()
            }

            sendUserEndCall()
            await performEndInterview(elapsedTime, messages)
        }

        videoTrack.addEventListener('ended', handleScreenShareStop)

        return () => {
            videoTrack.removeEventListener('ended', handleScreenShareStop)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayStreamRef.current, elapsedTime, messages.length, isMyraSpeaking])

    // Handle socket error - only if interview hasn't ended yet
    useEffect(() => {
        if (socketError && !isInterviewEndedRef.current) {
            console.error('❌ WebSocket Error detected:', socketError)
            isInterviewEndedRef.current = true // Mark as ended to prevent duplicate navigation
            navigate('/open-ai-down', {
                state: {
                    elapsedTime,
                    jobTitle
                }
            })
        }
    }, [socketError, elapsedTime, jobTitle, navigate])

    const handleEndInterview = () => {
        setIsVisibleEndInterviewOpen(true)
    }

    const handleCloseEndInterviewModal = () => {
        setIsVisibleEndInterviewOpen(false)
    }

    const handleConfirmEndInterview = async () => {
        isInterviewEndedRef.current = true // Mark interview as ended
        sendUserEndCall()
        await performEndInterview(elapsedTime, messages)
    }

    const toggleViewLiveTranscript = () => {
        setViewLiveTranscript(!viewLiveTranscript)
    }

    const handleToggleMicrophone = () => {
        toggleMicrophone(() => setShowMicWarningModal(true))
    }

    return (
        <div className="flex h-screen bg-gray-900">
            <div className="flex flex-col flex-1">
                <InterviewHeader
                    elapsedTime={elapsedTime}
                    jobTitle={jobTitle}
                    onToggleTranscript={toggleViewLiveTranscript}
                />

                <div className="flex flex-col flex-1 gap-5 px-6 pb-5 overflow-hidden bg-gray-800 md:flex-row">
                    <div className="relative flex-1 overflow-hidden">
                        <LocalPreview
                            videoRef={videoRef}
                            isCameraOn={isCameraOn}
                            isCameraStarting={isCameraStarting}
                            isMicOn={isMicOn}
                            isMyraSpeaking={isMyraSpeaking}
                            userName={userName}
                        />

                        <CallControls
                            isMicOn={isMicOn}
                            isMicStarting={isMicStarting}
                            isCameraOn={isCameraOn}
                            isCameraStarting={isCameraStarting}
                            onToggleMicrophone={handleToggleMicrophone}
                            onToggleCamera={toggleCamera}
                            onEndInterview={handleEndInterview}
                        />
                                </div>

                    <TranscriptPanel
                        viewLiveTranscript={viewLiveTranscript}
                        connected={connected}
                        isMyraSpeaking={isMyraSpeaking}
                        isAiTyping={isAiTyping}
                        isListening={isListening}
                        isMicOn={isMicOn}
                        messages={messages}
                    />
                                    </div>
                                </div>

            <MicWarningModal
                visible={showMicWarningModal}
                onClose={() => setShowMicWarningModal(false)}
            />

            <EndInterviewModal
                visible={isVisibleEndInterviewOpen}
                onClose={handleCloseEndInterviewModal}
                onConfirm={handleConfirmEndInterview}
            />
        </div>
    )
}

export default InterviewCallV2
